import { findCircularAlignment, interpolatePolygon, Polygon, Point } from './polygon-utils'

/** How many polygon positions to keep per key for steadiness checks */
export const STEADINESS_HISTORY_MAX_QUEUE_SIZE = 5
/** How many pixels a detection can move (per axis) before considered unsteady */
export const STEADINESS_MAX_PIXELS = 15
/** How much the longest edge should fill the smaller image dimension */
export const CLOSENESS_MIN_PERCENTAGE = 0.4

type PolygonHistoryItem = {
  polygon: Polygon
  centroid: Point
}

interface IStabilityTrackerOptions {
  steadinessTolerance?: number
  closenessThreshold?: number
}

export class StabilityTracker {
  private history: { [key: string]: PolygonHistoryItem[] } = {}
  private readonly steadinessToleranceSquared: number
  private readonly closenessThresholdSquared: number

  constructor(options?: IStabilityTrackerOptions) {
    const steadinessTolerance = options?.steadinessTolerance ?? STEADINESS_MAX_PIXELS
    this.steadinessToleranceSquared = steadinessTolerance * steadinessTolerance

    const closenessThreshold = options?.closenessThreshold ?? CLOSENESS_MIN_PERCENTAGE
    this.closenessThresholdSquared = closenessThreshold * closenessThreshold
  }

  public updateHistory(key: string, cornerPoints: Polygon): Polygon {
    const smoothed = this.interpolate(key, cornerPoints)

    if (!this.history[key]) {
      this.history[key] = []
    }

    this.history[key].push({
      polygon: smoothed,
      centroid: {
        x: smoothed.reduce((sum, p) => sum + p.x, 0) / smoothed.length,
        y: smoothed.reduce((sum, p) => sum + p.y, 0) / smoothed.length,
      },
    })

    if (this.history[key].length > STEADINESS_HISTORY_MAX_QUEUE_SIZE) {
      this.history[key].shift()
    }

    return smoothed
  }

  public computeSteadiness(key: string): boolean {
    const entries = this.history[key]
    if (!entries || entries.length < 2) {
      return false
    }

    for (let i = 1; i < entries.length; i++) {
      const prev = entries[i - 1]
      const curr = entries[i]

      const deltaX = Math.abs(curr.centroid.x - prev.centroid.x)
      const deltaY = Math.abs(curr.centroid.y - prev.centroid.y)
      const deltaSquared = deltaX * deltaX + deltaY * deltaY

      if (deltaSquared > this.steadinessToleranceSquared) {
        return false
      }
    }

    return true
  }

  /**
   * Returns true if the longest edge of the polygon fills at least CLOSENESS_MIN_PERCENTAGE of the
   * smaller image dimension. Defaults to true if dimensions are zero or unavailable.
   */
  public computeCloseness(
    cornerPoints: Polygon,
    imageWidth: number,
    imageHeight: number,
    thresholdOverride?: number
  ): boolean {
    if (cornerPoints.length === 0 || imageWidth === 0 || imageHeight === 0) {
      return true
    }

    let maxEdgeLengthSquared = 0
    for (let i = 0; i < cornerPoints.length; i++) {
      const j = (i + 1) % cornerPoints.length
      const dx = cornerPoints[j].x - cornerPoints[i].x
      const dy = cornerPoints[j].y - cornerPoints[i].y
      const edgeLengthSquared = dx * dx + dy * dy
      maxEdgeLengthSquared = Math.max(maxEdgeLengthSquared, edgeLengthSquared)
    }

    const minDimension = Math.min(imageWidth, imageHeight)
    const minDimensionSquared = minDimension * minDimension
    const dimensionPercentage = maxEdgeLengthSquared / minDimensionSquared

    const thresholdSquared =
      thresholdOverride !== undefined
        ? thresholdOverride * thresholdOverride
        : this.closenessThresholdSquared
    return dimensionPercentage >= thresholdSquared
  }

  /**
   * Removes history for keys not in activeKeys. Call after processing each
   * frame to clean up detections that are no longer present.
   */
  public pruneHistory(activeKeys: Set<string>): void {
    for (const key of Object.keys(this.history)) {
      if (!activeKeys.has(key)) {
        delete this.history[key]
      }
    }
  }

  private interpolate(key: string, cornerPoints: Polygon): Polygon {
    const entries = this.history[key]
    if (!entries?.length) {
      return cornerPoints
    }

    const lastItem = entries[entries.length - 1]
    const lastAligned = findCircularAlignment(lastItem.polygon, cornerPoints)

    const easeIn = (t: number): number => t * t

    return interpolatePolygon(lastAligned, cornerPoints, easeIn(0.8))
  }
}
